import { useState, useCallback } from 'react';
import { ToastProps, ToastType } from '../components/ui/Toast';
import { ProcessedError } from '../utils/errorHandler';

interface ToastOptions {
  duration?: number;
  canRetry?: boolean;
  onRetry?: () => void;
  suggestedActions?: string[];
}

interface ToastHook {
  toasts: ToastProps[];
  showToast: (type: ToastType, title: string, message?: string, options?: ToastOptions) => string;
  showError: (error: ProcessedError, onRetry?: () => void) => string;
  showSuccess: (title: string, message?: string, options?: ToastOptions) => string;
  showWarning: (title: string, message?: string, options?: ToastOptions) => string;
  showInfo: (title: string, message?: string, options?: ToastOptions) => string;
  dismissToast: (id: string) => void;
  clearAllToasts: () => void;
}

export const useToast = (): ToastHook => {
  const [toasts, setToasts] = useState<ToastProps[]>([]);

  const showToast = useCallback((
    type: ToastType,
    title: string,
    message?: string,
    options: ToastOptions = {}
  ): string => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const newToast: ToastProps = {
      id,
      type,
      title,
      message,
      duration: options.duration ?? 5000,
      canRetry: options.canRetry ?? false,
      suggestedActions: options.suggestedActions ?? [],
      onRetry: options.onRetry,
      onDismiss: dismissToast
    };

    setToasts(prev => [...prev, newToast]);
    return id;
  }, []);

  const showError = useCallback((error: ProcessedError, onRetry?: () => void): string => {
    return showToast('error', error.userMessage, error.technicalMessage, {
      duration: error.severity === 'critical' ? 0 : 8000, // Critical errors stay until dismissed
      canRetry: error.canRetry,
      suggestedActions: error.suggestedActions,
      onRetry
    });
  }, [showToast]);

  const showSuccess = useCallback((title: string, message?: string, options?: ToastOptions): string => {
    return showToast('success', title, message, { duration: 4000, ...options });
  }, [showToast]);

  const showWarning = useCallback((title: string, message?: string, options?: ToastOptions): string => {
    return showToast('warning', title, message, { duration: 6000, ...options });
  }, [showToast]);

  const showInfo = useCallback((title: string, message?: string, options?: ToastOptions): string => {
    return showToast('info', title, message, { duration: 5000, ...options });
  }, [showToast]);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  return {
    toasts,
    showToast,
    showError,
    showSuccess,
    showWarning,
    showInfo,
    dismissToast,
    clearAllToasts
  };
};
